import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { BannerStatus } from "@plex/react-components";

import { validationMessages } from "infrastructure/localization";
import { INotification, notification } from "infrastructure/notification";
import { getScreenExtraReducers } from "ui/app";
import { IPickerSearchCriterion } from "ui/shared/helpers/plex-components";

import { getMessageRelationshipsInitialState, IMessage, IMessageRelationshipsState, IRelationship } from "./MessageRelationships.state";

const initialState = getMessageRelationshipsInitialState();

export const messageRelationships = createSlice({
  initialState: initialState,
  name: "messageRelationships",
  reducers: {
    loadMessageDetails: (state: IMessageRelationshipsState, _: PayloadAction<string>) => {
      return { ...state, isLoading: true };
    },
    loadMessageDetailsCompleted: (state: IMessageRelationshipsState, action: PayloadAction<{ message: IMessage; notifications: INotification[] }>) => {
      return { ...state, isLoading: false, messageDetails: action.payload.message, newRelationship: [], selectedRelationship: [], notifications: action.payload.notifications };
    },
    loadMessageDetailsFailed: (state: IMessageRelationshipsState, action: PayloadAction<INotification>) => {
      return { ...state, isLoading: false, newRelationship: [], selectedRelationship: [], notifications: [action.payload] };
    },
    loadPickerMessages: (state: IMessageRelationshipsState, _: PayloadAction<IPickerSearchCriterion<IMessage>>) => {
      return { ...state, isLoading: true };
    },
    loadPickerMessagesCompleted: (state: IMessageRelationshipsState, action: PayloadAction<IMessage[]>) => {
      return { ...state, isLoading: false, messagesPickerData: action.payload.filter(m => m.messageId !== state.messageDetails?.messageId) };
    },
    loadPickerMessagesFailed: (state: IMessageRelationshipsState, action: PayloadAction<INotification>) => {
      return { ...state, isLoading: false, messagesPickerData: [], notifications: [action.payload] };
    },
    loadRelationships: (state: IMessageRelationshipsState, _: PayloadAction<string>) => {
      return { ...state, isLoading: true };
    },
    loadRelationshipsCompleted: (state: IMessageRelationshipsState, action: PayloadAction<IMessage[]>) => {
      return { ...state, isLoading: false, relationships: action.payload };
    },
    loadRelationshipsFailed: (state: IMessageRelationshipsState, action: PayloadAction<INotification>) => {
      return { ...state, isLoading: false, relationships: [], notifications: [action.payload] };
    },
    addRelationship: (state: IMessageRelationshipsState, _: PayloadAction<{ messageId: string; newRelationship: IMessage; version: number }>) => {
      return { ...state, isLoading: true };
    },
    addRelationshipCompleted: (state: IMessageRelationshipsState, action: PayloadAction<{ newRelationship: IMessage; notification: INotification; redirect: boolean }>) => {
      return {
        ...state,
        isLoading: false,
        messageDetails: {
          ...state.messageDetails,
          isParentMessage: true
        },
        newRelationship: [],
        relationships: [...state.relationships, action.payload.newRelationship],
        notifications: [action.payload.notification],
        shouldRedirect: action.payload.redirect
      };
    },
    addRelationshipFailed: (state: IMessageRelationshipsState, action: PayloadAction<INotification>) => {
      return { ...state, isLoading: false, notifications: [action.payload] };
    },
    deleteRelationship: (state: IMessageRelationshipsState, _: PayloadAction<IRelationship>) => {
      return { ...state, isLoading: true };
    },
    deleteRelationshipCompleted: (state: IMessageRelationshipsState, action: PayloadAction<{ removedRelationshipId: string; version: number; message: INotification }>) => {
      const relationships: IMessage[] = state.relationships.filter(t => t.messageId !== action.payload.removedRelationshipId);
      return {
        ...state,
        isLoading: false,
        messageDetails: {
          ...state.messageDetails,
          isParentMessage: relationships.length > 0,
          version: action.payload.version
        },
        relationships: relationships,
        selectedRelationship: [],
        notifications: [action.payload.message]
      };
    },
    deleteRelationshipFailed: (state: IMessageRelationshipsState, action: PayloadAction<INotification>) => {
      return { ...state, isLoading: false, selectedRelationship: [], notifications: [action.payload] };
    },
    selectNewRelationship: (state: IMessageRelationshipsState, action: PayloadAction<IMessage[]>) => {
      return { ...state, newRelationship: action.payload };
    },
    selectExistingRelationship: (state: IMessageRelationshipsState, action: PayloadAction<IMessage[]>) => {
      return { ...state, selectedRelationship: action.payload };
    },
    cancelLinkClick: (state: IMessageRelationshipsState) => {
      return { ...state, isLoading: true, shouldRedirect: true };
    },
    setValidationBanner: (state: IMessageRelationshipsState) => {
      return { ...state, notifications: [notification.warning().useMessageId(validationMessages.error("summary"))] };
    },
    clearValidationNotification: (state: IMessageRelationshipsState) => {
      return { ...state, notifications: [...state.notifications.filter(n => n.type !== BannerStatus.warning)] };
    }
  },
  extraReducers: getScreenExtraReducers<IMessageRelationshipsState>("notifications", initialState)
});
